import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, FileText, Loader2, Printer, X, Check } from 'lucide-react';
import jsPDF from 'jspdf';
import { useOutfitStore } from '@/stores/outfitStore';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogTitle,
} from '@/components/ui/dialog';
import { OutfitReportLayout, PDF_THEMES, REPORT_WIDTH } from './OutfitReportLayout';
import type { PdfTheme } from './OutfitReportLayout';

/* ── Helpers ── */

const hexToRgb = (hex: string): [number, number, number] => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return [r, g, b];
};

/** Fetch a remote image as base64 data-URL (null on failure/timeout) */
const fetchImageAsBase64 = async (url: string, timeoutMs = 8000): Promise<string | null> => {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    const res = await fetch(url, { mode: 'cors', signal: controller.signal });
    clearTimeout(timer);
    if (!res.ok) return null;
    const blob = await res.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

/* ── Component ── */

export const PDFExport: React.FC = () => {
  const { t } = useTranslation();
  const { currentEvent, outfits } = useOutfitStore();
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState<PdfTheme>(PDF_THEMES[0].theme);
  const [previewScale, setPreviewScale] = useState(1);

  const reportRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  /** Scale the 800px report to fit the mobile viewport */
  const computeScale = useCallback(() => {
    if (scrollAreaRef.current) {
      const w = scrollAreaRef.current.clientWidth;
      setPreviewScale(Math.min(1, (w - 16) / REPORT_WIDTH));
    }
  }, []);

  useEffect(() => {
    if (previewOpen) {
      const id = requestAnimationFrame(computeScale);
      window.addEventListener('resize', computeScale);
      return () => {
        cancelAnimationFrame(id);
        window.removeEventListener('resize', computeScale);
      };
    }
  }, [previewOpen, computeScale]);

  const handlePrint = () => window.print();

  /**
   * Generate PDF using jsPDF drawing API directly.
   * No html2canvas — 100% reliable regardless of screen size or CSS transforms.
   */
  const handleDownloadPDF = async () => {
    if (!currentEvent) return;
    setIsGenerating(true);

    try {
      /* Pre-fetch every image in parallel */
      const allUrls = new Set<string>();
      for (const outfit of outfits) {
        const imgArrays = [
          outfit.outfit_images,
          outfit.hair_images,
          outfit.jewelry_images,
          outfit.purse_images,
          outfit.shoes_images,
        ];
        for (const imgs of imgArrays) {
          if (imgs) imgs.forEach((u) => allUrls.add(u));
        }
      }

      const imageCache = new Map<string, string | null>();
      await Promise.all(
        Array.from(allUrls).map(async (url) => {
          imageCache.set(url, await fetchImageAsBase64(url));
        }),
      );

      /* PDF setup */
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageW = pdf.internal.pageSize.getWidth(); // ~210
      const pageH = pdf.internal.pageSize.getHeight(); // ~297
      const mx = 16;
      const contentW = pageW - mx * 2;
      const c = selectedTheme;

      const [pr, pg, pb] = hexToRgb(c.primary);
      const [sr, sg, sb] = hexToRgb(c.secondary);
      const [ar, ag, ab] = hexToRgb(c.accent);

      /* ── Cover page ── */
      pdf.setFillColor(sr, sg, sb);
      pdf.rect(0, 0, pageW, pageH, 'F');

      // Accent circle
      pdf.setFillColor(pr, pg, pb);
      pdf.circle(pageW / 2, 90, 10, 'F');
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(16);
      pdf.setTextColor(255, 255, 255);
      pdf.text('\u2726', pageW / 2, 93, { align: 'center' });

      // Event title
      pdf.setFont('helvetica', 'bolditalic');
      pdf.setFontSize(28);
      pdf.setTextColor(pr, pg, pb);
      const titleLines = pdf.splitTextToSize(currentEvent.name, contentW);
      pdf.text(titleLines, pageW / 2, 115, { align: 'center' });

      // Subtitle
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      pdf.setTextColor(ar, ag, ab);
      const subtitle = `FASHION PLAN  \u2022  ${currentEvent.total_days} ${t('outfit.day', 'Days').toUpperCase()}`;
      const subtitleY = 115 + titleLines.length * 10 + 10;
      pdf.text(subtitle, pageW / 2, subtitleY, { align: 'center' });

      // Sponsor line on cover
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      pdf.setTextColor(160, 160, 160);
      pdf.text('Sponsored by', pageW / 2, subtitleY + 30, { align: 'center' });
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      pdf.setTextColor(pr, pg, pb);
      pdf.text('Bedda Matri', pageW / 2, subtitleY + 36, { align: 'center' });
      pdf.setFont('helvetica', 'italic');
      pdf.setFontSize(7);
      pdf.setTextColor(180, 180, 180);
      pdf.text('beddamatri.shop', pageW / 2, subtitleY + 42, { align: 'center' });

      /* ── Day pages ── */
      for (const outfit of outfits) {
        const cats = [
          { label: t('outfit.title', 'Outfit'), notes: outfit.outfit, images: outfit.outfit_images },
          { label: t('outfit.hair', 'Hair'), notes: outfit.hair, images: outfit.hair_images },
          { label: t('outfit.jewelry', 'Jewelry'), notes: outfit.jewelry, images: outfit.jewelry_images },
          { label: t('outfit.purse', 'Purse'), notes: outfit.purse, images: outfit.purse_images },
          { label: t('outfit.shoes', 'Shoes'), notes: outfit.shoes, images: outfit.shoes_images },
        ];

        const hasAnything =
          cats.some((cat) => (cat.images?.length || 0) > 0 || !!cat.notes) || !!outfit.notes;
        if (!hasAnything) continue;

        pdf.addPage();

        // White background
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, 0, pageW, pageH, 'F');

        // Top accent stripe
        pdf.setFillColor(ar, ag, ab);
        pdf.rect(0, 0, pageW, 2.5, 'F');

        let y = 18;

        // Day header
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(22);
        pdf.setTextColor(pr, pg, pb);
        const dayLabel = `${t('outfit.day', 'Day')} ${outfit.day_number}`;
        pdf.text(dayLabel, mx, y);

        const dayW = pdf.getTextWidth(dayLabel);
        pdf.setFontSize(10);
        pdf.setTextColor(185, 185, 185);
        pdf.text(`/ ${currentEvent.total_days}`, mx + dayW + 3, y);

        // Event name right-aligned
        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(ar, ag, ab);
        pdf.text(currentEvent.name.toUpperCase(), pageW - mx, y, { align: 'right' });

        y += 6;

        // Separator
        pdf.setDrawColor(sr, sg, sb);
        pdf.setLineWidth(0.4);
        pdf.line(mx, y, pageW - mx, y);
        y += 8;

        // Categories
        for (const cat of cats) {
          if (!cat.notes && (!cat.images || cat.images.length === 0)) continue;

          // Category label
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(8);
          pdf.setTextColor(pr, pg, pb);
          pdf.text(cat.label.toUpperCase(), mx, y);
          y += 5;

          // Thumbnails
          if (cat.images && cat.images.length > 0) {
            const thumbSize = 24;
            const gap = 3;
            let x = mx;

            for (const url of cat.images) {
              // Gray placeholder background
              pdf.setFillColor(240, 240, 240);
              pdf.roundedRect(x, y, thumbSize, thumbSize, 1.5, 1.5, 'F');

              // Thin border
              pdf.setDrawColor(sr, sg, sb);
              pdf.setLineWidth(0.3);
              pdf.roundedRect(x, y, thumbSize, thumbSize, 1.5, 1.5, 'S');

              const base64 = imageCache.get(url);
              if (base64) {
                try {
                  const fmt = base64.startsWith('data:image/png') ? 'PNG' : 'JPEG';
                  pdf.addImage(base64, fmt, x + 0.5, y + 0.5, thumbSize - 1, thumbSize - 1);
                } catch {
                  // placeholder already drawn
                }
              }
              x += thumbSize + gap;
            }
            y += thumbSize + 3;
          }

          // Notes
          if (cat.notes) {
            pdf.setFont('helvetica', 'italic');
            pdf.setFontSize(7);
            pdf.setTextColor(140, 140, 140);
            const noteText = cat.notes.length > 120 ? cat.notes.slice(0, 120) + '\u2026' : cat.notes;
            const noteLines = pdf.splitTextToSize(noteText, contentW);
            pdf.text(noteLines, mx, y);
            y += noteLines.length * 3.2 + 1;
          }

          y += 5;
        }

        // General notes box
        if (outfit.notes) {
          y += 2;
          const noteLines = pdf.splitTextToSize(outfit.notes, contentW - 10);
          const boxH = Math.max(10, noteLines.length * 3.5 + 7);
          pdf.setFillColor(sr, sg, sb);
          pdf.roundedRect(mx, y, contentW, boxH, 2, 2, 'F');
          pdf.setFont('helvetica', 'italic');
          pdf.setFontSize(7);
          pdf.setTextColor(100, 100, 100);
          pdf.text(noteLines, mx + 5, y + 5);
        }
      }

      pdf.save(`${currentEvent.name.replace(/\s+/g, '_')}_Outfit_Plan.pdf`);
    } catch (error) {
      console.error('PDF generation error:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const hasContent = outfits.some(
    (o) =>
      (o.hair_images?.length || 0) > 0 ||
      (o.jewelry_images?.length || 0) > 0 ||
      (o.purse_images?.length || 0) > 0 ||
      (o.shoes_images?.length || 0) > 0 ||
      (o.outfit_images?.length || 0) > 0 ||
      !!o.notes,
  );

  if (!hasContent) return null;

  return (
    <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="w-full flex items-center gap-3 border-border text-foreground bg-white hover:bg-secondary/60 rounded-xl h-12 md:h-11 font-medium tracking-wide shadow-sm hover:shadow-md transition-all active:scale-95"
        >
          <FileText className="w-5 h-5 md:w-4 md:h-4" />
          {t('export.pdf', 'Preview & PDF')}
        </Button>
      </DialogTrigger>

      <DialogContent className="max-w-[100vw] w-[100vw] h-[100dvh] max-h-[100dvh] p-0 m-0 rounded-none border-none flex flex-col bg-background overflow-hidden print:w-full print:h-auto print:max-h-none print:overflow-visible print:bg-white print:static">
        <DialogTitle className="sr-only">
          {t('export.pdf', 'Outfit Plan Preview')}
        </DialogTitle>

        {/* Top bar */}
        <div className="print:hidden w-full bg-white/90 backdrop-blur-md border-b border-border flex items-center justify-between px-4 py-2.5 shadow-sm z-10 shrink-0">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setPreviewOpen(false)}
              className="rounded-full hover:bg-secondary w-9 h-9"
            >
              <X className="w-5 h-5 text-muted-foreground" />
            </Button>
            <span className="font-serif italic font-bold text-sm text-foreground hidden sm:inline truncate max-w-[180px]">
              {currentEvent?.name}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Button
              onClick={handlePrint}
              variant="outline"
              className="rounded-xl border-border h-9 px-3 text-sm font-medium"
            >
              <Printer className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline">{t('export.print', 'Stampa')}</span>
            </Button>
            <Button
              onClick={handleDownloadPDF}
              disabled={isGenerating}
              className="rounded-xl bg-gradient-to-r from-[#E91E8C] to-primary hover:opacity-90 text-white shadow-md h-9 px-4 text-sm font-bold active:scale-95 transition-all"
            >
              {isGenerating ? (
                <Loader2 className="w-4 h-4 animate-spin sm:mr-1.5" />
              ) : (
                <Download className="w-4 h-4 sm:mr-1.5" />
              )}
              <span className="hidden sm:inline">
                {t('export.download', 'Download PDF')}
              </span>
            </Button>
          </div>
        </div>

        {/* Theme picker strip */}
        <div className="print:hidden bg-white border-b border-border px-4 py-2 flex items-center gap-3 shrink-0">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap">
            {t('export.theme', 'Tema')}:
          </span>
          <div className="flex items-center gap-2">
            {PDF_THEMES.map((item) => {
              const isActive = selectedTheme.primary === item.theme.primary;
              return (
                <button
                  key={item.name}
                  onClick={() => setSelectedTheme(item.theme)}
                  aria-label={item.name}
                  aria-pressed={isActive}
                  className="relative w-7 h-7 rounded-full border-2 transition-all active:scale-90 flex items-center justify-center"
                  style={{
                    backgroundColor: item.theme.primary,
                    borderColor: isActive ? '#fff' : 'transparent',
                    boxShadow: isActive
                      ? `0 0 0 2px ${item.theme.primary}`
                      : 'none',
                  }}
                >
                  {isActive && <Check className="w-3 h-3 text-white" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Scrollable preview — scaled to fit mobile */}
        <div
          ref={scrollAreaRef}
          className="flex-1 w-full overflow-y-auto overflow-x-hidden bg-secondary/30 print:overflow-visible print:w-full print:h-auto print:absolute print:inset-0"
        >
          <div
            style={{
              padding: '16px 0',
              transformOrigin: 'top center',
              transform: `scale(${previewScale})`,
              width: REPORT_WIDTH,
              margin: '0 auto',
            }}
          >
            {currentEvent && (
              <OutfitReportLayout
                ref={reportRef}
                event={currentEvent}
                outfits={outfits}
                theme={selectedTheme}
              />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
